import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { showFindAListenerCta } from '../../util/accountRole';
import { FormattedMessage } from '../../util/reactIntl';

import NamedLink from '../NamedLink/NamedLink';

import css from './InfoPageChrome.module.css';

const canGoBack = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return false;
  }
  try {
    const ref = document.referrer;
    if (!ref) {
      return false;
    }
    const url = new URL(ref);
    return url.origin === window.location.origin && url.pathname !== window.location.pathname;
  } catch (e) {
    return false;
  }
};

/**
 * Top back on static/info pages. Signed-in people get ‹ Back to Dashboard.
 * Guests use the previous same-origin page, then Home.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const InfoPageBack = props => {
  const { className } = props;
  const history = useHistory();
  const currentUser = useSelector(state => state.user?.currentUser);
  const signedIn = !!currentUser?.id;

  if (signedIn) {
    return (
      <NamedLink
        className={classNames(css.back, className)}
        name="DashboardPage"
        data-testid="info-page-back"
      >
        <span className={css.backChevron} aria-hidden="true">
          ‹
        </span>
        <FormattedMessage id="BackToDashboard.label" />
      </NamedLink>
    );
  }

  const onBack = () => {
    if (canGoBack()) {
      history.goBack();
      return;
    }
    history.push('/');
  };

  return (
    <button
      type="button"
      className={classNames(css.back, className)}
      onClick={onBack}
      data-testid="info-page-back"
    >
      <span className={css.backChevron} aria-hidden="true">
        ‹
      </span>
      <FormattedMessage id="InfoPage.back" />
    </button>
  );
};

/**
 * Bottom next-step CTA on static/info pages.
 *
 * @param {Object} props
 * @param {boolean} [props.forceBook] Pricing always offers Find a listener.
 * @returns {JSX.Element}
 */
export const InfoPageCta = props => {
  const { className, forceBook = false } = props;
  const currentUser = useSelector(state => state.user?.currentUser);
  const signedIn = !!currentUser?.id;
  const showFind =
    forceBook ||
    !signedIn ||
    showFindAListenerCta({ currentUser, isAuthenticated: signedIn });

  if (showFind) {
    return (
      <NamedLink
        className={classNames(css.cta, className)}
        name="SearchPage"
        data-testid="info-page-cta"
      >
        <FormattedMessage id="InfoPage.findListener" />
      </NamedLink>
    );
  }

  return (
    <NamedLink
      className={classNames(css.cta, css.ctaQuiet, className)}
      name="DashboardPage"
      data-testid="info-page-cta"
    >
      <FormattedMessage id="InfoPage.goDashboard" />
    </NamedLink>
  );
};

/**
 * Back at the top and a next-step CTA at the bottom.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
const InfoPageChrome = props => {
  const { children, currentPage, forceBook = false } = props;
  return (
    <div className={css.root} data-info-chrome={currentPage || true}>
      <InfoPageBack />
      {children}
      <InfoPageCta forceBook={forceBook} />
    </div>
  );
};

export default InfoPageChrome;
